import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { MEMBER_ONBOARDING } from "../data/content";

export default function MemberGuide() {
  const { hero, intro, steps, faqs } = MEMBER_ONBOARDING;
  return (
    <>
      <SEO
        title="New Member Guide — How to Use Your Membership"
        description="Just bought a timeshare or travel club membership? Our plain-English, step-by-step guide helps confused new owners activate, understand, and book their first trip with zero stress."
        path="/members"
        image={hero.image}
      />

      {/* HERO */}
      <section className="relative flex min-h-[58vh] items-end overflow-hidden">
        <img src={hero.image} alt={hero.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-semibold text-white">
              <Sparkles className="h-4 w-4" /> New Members Start Here
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              You bought it. Now let's actually use it.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">{intro}</p>
          </motion.div>
        </div>
      </section>

      {/* STEPS */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>The Roadmap</Overline>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            Six steps from confused to confident.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <article className="flex h-full flex-col rounded-3xl card-3d p-8">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 font-display text-2xl font-black text-primary">
                  {i + 1}
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-navy">{s.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-600">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted section-pad">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <Overline>Common Confusions</Overline>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              The questions every new owner asks.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <Accordion type="single" collapsible className="mt-10 space-y-4">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  data-testid={`member-faq-${i}`}
                  className="overflow-hidden rounded-2xl border border-slate-100 bg-white px-6 shadow-sm"
                >
                  <AccordionTrigger className="py-5 text-left font-display text-lg font-semibold text-navy hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center gap-6 rounded-[2rem] bg-navy p-10 text-center sm:flex-row sm:justify-between sm:text-left">
            <div>
              <h3 className="font-display text-2xl font-bold text-white">
                Want the deeper tactics?
              </h3>
              <p className="mt-2 text-slate-300">
                Our Guides break down points, fees, and booking tiers step by step.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/guides"
                data-testid="member-guides-cta"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground btn-glow hover:bg-primary/90"
              >
                Read the Guides <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/travel-club-reviews"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-semibold text-white hover:bg-white/20"
              >
                Compare Clubs
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
